import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { NovaMensagemNotification, MessageAckNotification, ConversaTransferidaNotification } from '../models/mensagem.model';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private hubConnection?: signalR.HubConnection;
  // Remove '/api' da URL base para o SignalR (ex: https://localhost:44393/api -> https://localhost:44393)
  private apiUrl = environment.apiUrl.endsWith('/api') 
    ? environment.apiUrl.replace('/api', '') 
    : environment.apiUrl.replace(/\/api\/?$/, '');
  private novaMensagemSubject = new BehaviorSubject<NovaMensagemNotification | null>(null);
  public novaMensagem$ = this.novaMensagemSubject.asObservable();
  
  private mensagemEnviadaSubject = new BehaviorSubject<any | null>(null);
  public mensagemEnviada$ = this.mensagemEnviadaSubject.asObservable();

  private messageAckSubject = new BehaviorSubject<MessageAckNotification | null>(null);
  public messageAck$ = this.messageAckSubject.asObservable();

  private conversaTransferidaSubject = new BehaviorSubject<ConversaTransferidaNotification | null>(null);
  public conversaTransferida$ = this.conversaTransferidaSubject.asObservable();

  constructor() {}

  async startConnection(tenantId: string, atendenteId?: string): Promise<void> {
    const hubUrl = `${this.apiUrl}/notificationHub`;
    
    if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    const token = localStorage.getItem('accessToken');
    
    if (!token) {
      console.warn('SignalR: Token de acesso não encontrado no localStorage');
      return;
    }

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => {
          const accessToken = localStorage.getItem('accessToken') || '';
          if (!accessToken) {
            console.warn('SignalR: Token não encontrado ao criar conexão');
          }
          return accessToken;
        },
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
        }
      })
      .build();

    this.registerHandlers();
    this.registerTransportHandlers();

    try {
      await this.hubConnection.start();
      
      await this.hubConnection.invoke('JoinTenantGroup', tenantId);

      if (atendenteId) {
        await this.hubConnection.invoke('JoinAtendenteGroup', atendenteId);
      }
    } catch (error: any) {
      console.error('❌ Error starting SignalR connection:', error);
      
      if (error?.message) {
        const errorMsg = error.message.toLowerCase();
        if (errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
          console.error('❌ SignalR: Unauthorized (401) - Token inválido ou expirado');
        } else if (errorMsg.includes('403') || errorMsg.includes('forbidden')) {
          console.error('❌ SignalR: Forbidden (403) - Acesso negado');
        } else if (errorMsg.includes('404') || errorMsg.includes('not found')) {
          console.error('❌ SignalR: Not Found (404) - Hub não encontrado');
        } else if (errorMsg.includes('network') || errorMsg.includes('networkerror')) {
          console.error('❌ SignalR: Network Error - Verifique a conexão');
        }
      }
    }
  }

  private registerHandlers(): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.on('NewMessage', (data: NovaMensagemNotification) => {
      this.novaMensagemSubject.next(data);
    });

    this.hubConnection.on('MessageSent', (data: any) => {
      this.mensagemEnviadaSubject.next(data);
    });

    this.hubConnection.on('MessageAck', (data: MessageAckNotification) => {
      console.log('MessageAck recebido:', data);
      this.messageAckSubject.next(data);
    });

    this.hubConnection.on('ConversaTransferida', (data: ConversaTransferidaNotification) => {
      console.log('ConversaTransferida recebida:', data);
      this.conversaTransferidaSubject.next(data);
    });

    this.hubConnection.onreconnecting((error) => {
      if (error) {
        console.warn('SignalR reconectando...', error.message);
      }
    });

    this.hubConnection.onreconnected(() => {
      // Reconexão bem-sucedida
    });

    this.hubConnection.onclose((error) => {
      if (error) {
        console.error('SignalR desconectado:', error.message);
      }
    });
  }

  /**
   * Registrar handlers específicos para eventos de transporte
   */
  private registerTransportHandlers(): void {
    if (!this.hubConnection) {
      return;
    }

    try {
      const connection = (this.hubConnection as any).connection;
      
      if (connection) {
        connection.onTransportError = (error: any) => {
          console.error('SignalR: Transport error:', error?.message);
        };

        connection.onTransportFailed = (error: any) => {
          console.error('SignalR: Transport failed:', error?.message);
        };

        connection.onError = (error: any) => {
          console.error('SignalR: Connection error:', error?.message);
        };
      }
    } catch (e) {
      // Ignorar erro ao registrar handlers de transporte
    }
  }

  async stopConnection(tenantId: string, atendenteId?: string): Promise<void> {
    if (this.hubConnection) {
      try {
        await this.hubConnection.invoke('LeaveTenantGroup', tenantId);
        
        if (atendenteId) {
          await this.hubConnection.invoke('LeaveAtendenteGroup', atendenteId);
        }
        
        await this.hubConnection.stop();
      } catch (error: any) {
        console.error('Error stopping SignalR connection:', error);
      }
    }
  }

  getConnectionState(): signalR.HubConnectionState | undefined {
    return this.hubConnection?.state;
  }
  
  /**
   * Método para debug: retorna informações completas da conexão
   */
  getConnectionInfo(): any {
    return {
      estado: this.hubConnection?.state,
      connectionId: this.hubConnection?.connectionId,
      hubUrl: `${this.apiUrl}/notificationHub`,
      apiUrl: this.apiUrl,
      environmentApiUrl: environment.apiUrl,
      tokenDisponivel: !!localStorage.getItem('accessToken'),
      tokenLength: localStorage.getItem('accessToken')?.length || 0
    };
  }
}
